import type { AxiosError } from 'axios';
import type { ApiError, ApiValidationError } from '@/types/api';

/**
 * Custom API Error class
 * Handles errors from Django REST Framework backend
 */
export class AppError extends Error {
  public readonly statusCode: number;
  public readonly extra: Record<string, unknown>;
  public readonly isValidationError: boolean;
  public readonly fieldErrors: Record<string, string[]> | undefined;

  constructor(
    message: string,
    statusCode: number = 500,
    extra: Record<string, unknown> = {},
    fieldErrors?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'AppError';
    this.statusCode = statusCode;
    this.extra = extra;
    this.isValidationError = !!fieldErrors;
    this.fieldErrors = fieldErrors;

    // Maintains proper stack trace for where our error was thrown (V8 only)
    const ErrorWithCapture = Error as typeof Error & {
      captureStackTrace?: (error: Error, constructor: unknown) => void;
    };
    ErrorWithCapture.captureStackTrace?.(this, AppError);
  }

  /**
   * Create AppError from Axios error
   */
  static fromAxiosError(error: AxiosError<ApiError | ApiValidationError>): AppError {
    const status = error.response?.status ?? 500;
    const data = error.response?.data;

    // No response data - network error or timeout
    if (!data) {
      if (error.code === 'ECONNABORTED') {
        return new AppError('La solicitud ha excedido el tiempo de espera', 408);
      }
      if (error.code === 'ERR_NETWORK') {
        return new AppError('Error de conexión. Verifica tu conexión a internet.', 0);
      }
      return new AppError('Error de conexión con el servidor', 500);
    }

    // Validation error
    if (status === 400 && 'extra' in data && 'fields' in (data.extra as Record<string, unknown>)) {
      const validationData = data as ApiValidationError;
      return new AppError(validationData.message, status, validationData.extra, validationData.extra.fields);
    }

    // Standard API error
    return new AppError(data.message, status, data.extra);
  }

  /**
   * Get first error message for a field (useful for forms)
   */
  getFieldError(field: string): string | undefined {
    return this.fieldErrors?.[field]?.[0];
  }

  /**
   * Check if error is a specific HTTP status
   */
  isStatus(status: number): boolean {
    return this.statusCode === status;
  }

  /**
   * Check if error is unauthorized (401)
   */
  isUnauthorized(): boolean {
    return this.statusCode === 401;
  }

  /**
   * Check if error is forbidden (403)
   */
  isForbidden(): boolean {
    return this.statusCode === 403;
  }

  /**
   * Check if error is not found (404)
   */
  isNotFound(): boolean {
    return this.statusCode === 404;
  }
}

/**
 * Type guard to check if error is AppError
 */
export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}

/**
 * Extract error message from unknown error
 */
export function getErrorMessage(error: unknown): string {
  if (isAppError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'Ha ocurrido un error inesperado';
}
